import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { LoadingPanel } from "../../LoadingPanel";
import { addNotificationWithTimeout } from '../../../app/addNotificationWithTimeout';
import { handleResponse } from '../../../app/handleResponse';
import { getCSRFToken } from '../../../app/getCSRFToken';
import {
    Grid,
    GridColumn as Column,
} from "@progress/kendo-react-grid";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Label } from "@progress/kendo-react-labels";
import {
  Chart,
  ChartTitle,
  ChartSeries,
  ChartSeriesItem,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
} from "@progress/kendo-react-charts";
import "hammerjs";

import './VenueEvent.scss';

export const AdministratorVenueEventTicketCounts = (props) => {
    const dispatch = useDispatch();

    const defaultSection = {
        value: "",
        name: "All Sections",
    };

    const [venueEventTicketCounts, setVenueEventTicketCounts] = useState([]);
    const [venueEventTicketCountTotal, setVenueEventTicketCountTotal] = useState(0);
    const [skipCount, setSkipCount] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(1);
    const [areVenueEventTicketCountsLoading, setAreVenueEventTicketCountsLoading] = useState(true);
    const [loadingError, setLoadingError] = useState("");
    const [sections, setSections] = useState([defaultSection]);
    const [selectedSection, setSelectedSection] = useState(defaultSection);
    const [chartValue, setChartValues] = useState([]);
    const [chartDates, setChartDates] = useState([]);
    const [chartTitle, setChartTitle] = useState("Ticket Counts");

    const getVenueEventTicketCounts = (section, newPageNumber) => {
        setAreVenueEventTicketCountsLoading(true);

        const dataItem = {
            venueEventKey: props.venueEventKey,
            pageNumber: newPageNumber,
            sectionName: section?.value,
        };
    
        fetch(`/api/administrator/venueeventticketcounts`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setItemsPerPage(result.venueEventTicketCountsPerPage);
                setSkipCount(result.venueEventTicketCountsPerPage * (result.pageNumber - 1));
                setVenueEventTicketCountTotal(result.totalVenueEventTicketCount);                
                setVenueEventTicketCounts(result.venueEventTicketCounts);

                setAreVenueEventTicketCountsLoading(false);    
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setAreVenueEventTicketCountsLoading(false);
                setLoadingError(error.message);
            });
    };

    const getChartData = (section) => {
        const sectionName = section?.value;

        if (!sectionName) {
            setChartValues([]);
            setChartDates([]);
            return;
        }

        const dataItem = {
            venueEventKey: props.venueEventKey,
            sectionName: sectionName,
        };
    
        fetch(`/api/administrator/venueeventticketcountchartdata`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setChartDates(result.dates);
                setChartValues(result.values);
                setChartTitle(`Ticket Counts (${sectionName})`);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    };

    const getVenueEventTicketCountSectionNames = () => {
        const dataItem = {
            venueEventKey: props.venueEventKey
        };
    
        fetch(`/api/administrator/venueeventticketcountsectionnames`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {     
                const sectionsWithoutDefault = result.map(item => ({
                    value: item,
                    name: item,
                }));
                const allSections = [defaultSection].concat(sectionsWithoutDefault);

                setSections(allSections);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    };

    const pageChange = (event) => {
        const newPageNumber = (event.page.skip / event.page.take) + 1;
        getVenueEventTicketCounts(selectedSection, newPageNumber);
    };

    const sectionChanged = (event) => {
        const value = event.value;
        
        setSelectedSection(value);
        getVenueEventTicketCounts(value);
        getChartData(value);
    };

    useEffect(() => {
        if (props.venueEventKey) {
            getVenueEventTicketCountSectionNames();
            getVenueEventTicketCounts();
        }
    }, []);

    return (
        <div>
            <div className="administratorVenueEventPerformerAdd">
                <Label>Section:</Label> 

                <DropDownList
                    data={sections}
                    textField="name"
                    dataItemKey="value"
                    onChange={sectionChanged}
                    value={selectedSection}
                />
            </div>
            {areVenueEventTicketCountsLoading ? <LoadingPanel /> : (loadingError === "" ? (
                <div className="mb-3">
                    <Grid
                        data={venueEventTicketCounts}
                        resizable={true}
                        skip={skipCount}
                        take={itemsPerPage}
                        total={venueEventTicketCountTotal}
                        pageable={true}
                        onPageChange={pageChange}
                        scrollable="none"
                    >
                        <Column field="dateCreated" title={"Date"} />
                        <Column field="sectionName" title={"Section Name"} />
                        <Column field="ticketCount" title={"Ticket Count"} />
                    </Grid>
                </div>
            ) : <></>)
            }
            {chartDates.length === 0 ? <></> :
                <Chart>
                    <ChartTitle text={chartTitle} />
                    <ChartCategoryAxis>
                        <ChartCategoryAxisItem
                            title={{
                                text: "Dates",
                            }}
                            labels={{
                                rotation: "auto",
                            }}
                            categories={chartDates}
                            maxDivisions={20}
                        />
                    </ChartCategoryAxis>
                    <ChartSeries>
                        <ChartSeriesItem type="line" data={chartValue} />
                    </ChartSeries>
                </Chart>
            }
        </div>
    );
}